import { Globe, Lock } from 'lucide-react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { NotificationBadge } from '@/components/ui/notification-badge';
import type { TodoListResponse } from '@/lib/api';

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'Updated just now';
  if (diffMin < 60) return `Updated ${diffMin}m ago`;
  if (diffHr < 24) return `Updated ${diffHr}h ago`;
  if (diffDays < 30) return `Updated ${diffDays}d ago`;
  return `Updated ${new Date(dateString).toLocaleDateString()}`;
}

interface ListCardProps {
  list: TodoListResponse;
  onClick: () => void;
  /** Unread assignment notifications for items/sections in this list. */
  unreadCount?: number;
}

export function ListCard({ list, onClick, unreadCount = 0 }: ListCardProps) {
  return (
    <Card
      className="group cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all"
      role="button"
      tabIndex={0}
      aria-label={`Todo list: ${list.name}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onClick();
      }}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold truncate">{list.name}</h3>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <NotificationBadge
              count={unreadCount}
              aria-label={`${unreadCount} new ${unreadCount === 1 ? 'assignment' : 'assignments'}`}
            />
            {list.isPublic ? (
              <>
                <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                <Badge variant="secondary" className="text-xs">
                  Public
                </Badge>
              </>
            ) : (
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {list.description && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{list.description}</p>
        )}
        <p className="text-xs text-muted-foreground">{formatRelativeTime(list.updatedAt)}</p>
      </CardContent>
    </Card>
  );
}
